import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  CfoInsightsResponse,
  MetricComparisonKind,
  MetricComparisonResponse,
  QuickBooksArCustomerConcern,
  QuickBooksArTrendAnalysis,
  QuickBooksArTrendPeriod,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { QBO_AR_ANALYSIS_TABLE, cfoArPerfLog } from '../quickBooksArArtifact'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CfoDepthSection } from './CfoDepthSection'
import { ArAgingBridgeChart } from './charts'
import { CfoAiRead, CfoControlRow, CfoSegmentedControl } from './CfoPagePrimitives'
import { formatAsOf, formatCurrencyCompact, trimStr } from './cfoFormat'
import { CFO_SURFACES } from './cfoTelemetry'
import { useCfoBootstrap } from './useCfoBootstrap'
import type { CfoShellSelectOption } from './CfoPageShell'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { CfoArTrendChart } from './CfoArTrendChart'
import { downloadCfoArTableExcel } from './cfoArExcelExport'
import { PinToReportButton } from './presentation/useAddToReport'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'
import type { CfoInstantCard } from './analysis/types'
import {
  CfoArChangeDriversSection,
  CfoArCollectabilitySection,
  CfoArCustomersSection,
  CfoArIncomeAccountFilter,
  CfoArIncomeAccountSection,
  CfoArInvoicesSection,
  type CfoArExportTable,
} from './CfoArSections'
import {
  applyArBasis,
  completeArAnalysis,
  arAgingBuckets,
  arCollectabilityView,
  arCustomerKey,
  arCustomerRowsForPeriod,
  arExcludedArAmount,
  arExcludedKeySet,
  arExcludedSummary,
  buildArAgingBridge,
  arFilterInvoicesByIncomeAccounts,
  arInvoiceRowsForSelection,
  arIncomeAccountChangeDrivers,
  arIncomeAccountCustomerRowsForPeriod,
  arIncomeAccountTrendPeriods,
  arIncomeAccountRowsForPeriod,
  arPeriodEndDate,
  arReserveSignal,
  arHeadlineDsoAnchor,
  buildArAnalysisSelection,
  buildArCustomerChangeDrivers,
  formatArCurrency,
  formatArDays,
  formatArTrendPeriodLabel,
  numberOrNull,
  selectArPeriod,
  type ArBasis,
} from './cfoArView'

const SURFACE = CFO_SURFACES.ar

const PERIOD_COUNT = 13
const arBootstrapErrorMessage = (error: any): string => {
  const status = Number(error?.status || 0)
  const message = trimStr(error?.body || error?.message || error)
  if (status === 401 || /unauthorized|auth|session|token/i.test(message)) {
    return 'A/R analysis needs an active FuseGrid session before it can prepare the accounting artifact. Please sign in again and reload this workbook.'
  }
  if (status === 403) {
    return 'A/R analysis could not verify access to this workbook. Check that your user has access to the workbook and accounting connection.'
  }
  return message
    ? `A/R analysis could not load the CFO Core workbook bootstrap: ${message}`
    : 'A/R analysis could not load the CFO Core workbook bootstrap.'
}

const arLoadErrorLead = (message: string): string => {
  const text = trimStr(message)
  if (/could not reach the artifact prepare service|failed to fetch|network|timed out|protocol/i.test(text)) {
    return 'A/R analysis could not be reached.'
  }
  if (/no quickbooks a\/r activity|no invoice-based a\/r|no .*receivables detail|no .*receivable/i.test(text)) {
    return 'No A/R activity is available for this company.'
  }
  if (/being prepared|being refreshed|refreshing cfo analytics/i.test(text)) return 'A/R for this month is still being prepared.'
  return 'A/R for this month is unavailable.'
}

// The backend reports a calm "no data for this period" signal when the selected
// period is beyond the company's actual A/R history (the data genuinely stops
// earlier). This is an empty state, not a failure, so the page presents it
// without the error styling.
const isArNoDataForPeriod = (message: string): boolean =>
  /no a\/r activity exists for /i.test(trimStr(message))

const excludedStorageKey = (workbookId: string, connectionId: string): string =>
  `fg:cfo-core:ar:excluded:${workbookId || 'default'}:${connectionId || 'default'}`

const incomeAccountStorageKey = (workbookId: string, connectionId: string): string =>
  `fg:cfo-core:ar:income-accounts:${workbookId || 'default'}:${connectionId || 'default'}`

const arPeriodFingerprint = (periods: any[], selectedPeriod: any): string =>
  [
    SURFACE,
    trimStr(selectedPeriod?.periodEnd || selectedPeriod?.period || selectedPeriod?.periodStart),
    periods.length,
    periods.slice(-3).map((period) => [
      trimStr(period?.periodEnd || period?.period),
      numberOrNull(period?.endingAr) ?? 0,
      numberOrNull(period?.over90Ar) ?? 0,
      numberOrNull(period?.dso) ?? 0,
    ].join(':')).join('|'),
  ].join(':')

const arMetricTrend = (periods: any[], field: string, label: string) => {
  const points = periods.slice(-12).map((period) => {
    const value = numberOrNull(period?.[field])
    return {
      label: formatArTrendPeriodLabel(period) || trimStr(period?.periodEnd || period?.period),
      period: trimStr(period?.periodEnd || period?.periodStart || period?.period) || null,
      rawValue: value,
      value: value == null ? '-' : field === 'dso' || /age|days/i.test(label) ? formatArDays(value) : formatArCurrency(value),
    }
  })
  return {
    label: points.length >= 12 ? '12-month trend' : 'Available trend evidence',
    points,
    unavailable_reason: points.length >= 12 ? null : `A/R artifact has ${points.length} trend periods for ${label}.`,
  }
}

const arPctChange = (current: number | null, prior: number | null): string => {
  if (current == null || prior == null || Math.abs(prior) < 0.005) return '-'
  const pct = (current - prior) / Math.abs(prior)
  const sign = pct > 0 ? '+' : pct < 0 ? '-' : ''
  return `${sign}${Math.abs(pct * 100).toFixed(1)}%`
}

const arComparisonRow = (label: string, current: number | null, prior: number | null, field: string) => {
  const isDays = field === 'dso' || /age|days/i.test(field)
  const delta = current != null && prior != null ? current - prior : null
  const currentText = current == null ? '-' : isDays ? formatArDays(current) : formatArCurrency(current)
  const priorText = prior == null ? '-' : isDays ? formatArDays(prior) : formatArCurrency(prior)
  return {
    label,
    current: currentText,
    prior: priorText,
    change: isDays && delta != null ? `${delta >= 0 ? '+' : '-'}${Math.abs(delta).toFixed(0)}d` : arPctChange(current, prior),
    tone: delta == null || Math.abs(delta) < 0.005 ? 'neutral' : isDays || field !== 'endingAr' ? (delta > 0 ? 'bad' : 'good') : (delta > 0 ? 'good' : 'bad'),
    unavailableReason: current == null || prior == null ? 'Not available in the A/R artifact window.' : null,
  }
}

const arMetricComparisonRows = (periods: any[], metricPeriod: any, field: string) => {
  const selectedKey = trimStr(metricPeriod?.periodEnd || metricPeriod?.period || metricPeriod?.periodStart)
  const currentIndex = Math.max(0, periods.findIndex((period) => (
    trimStr(period?.periodEnd || period?.period || period?.periodStart) === selectedKey
  )))
  const valueAt = (index: number): number | null => index >= 0 && index < periods.length ? numberOrNull(periods[index]?.[field]) : null
  const sumWindow = (indices: number[]): number | null => {
    let total = 0
    let found = false
    for (const index of indices) {
      const value = valueAt(index)
      if (value == null) continue
      total += value
      found = true
    }
    return found ? total : null
  }
  const current = periods[currentIndex] || metricPeriod
  const currentPeriod = trimStr(current?.periodEnd || current?.period || '')
  const currentYear = Number(currentPeriod.slice(0, 4))
  const currentMonth = Number(currentPeriod.slice(5, 7))
  const ytdIndices = periods
    .map((period, index) => ({ period: trimStr(period?.periodEnd || period?.period || ''), index }))
    .filter(({ period, index }) => Number(period.slice(0, 4)) === currentYear && Number(period.slice(5, 7)) <= currentMonth && index <= currentIndex)
    .map(({ index }) => index)
  const priorYtdIndices = periods
    .map((period, index) => ({ period: trimStr(period?.periodEnd || period?.period || ''), index }))
    .filter(({ period }) => Number(period.slice(0, 4)) === currentYear - 1 && Number(period.slice(5, 7)) <= currentMonth)
    .map(({ index }) => index)
  return [
    arComparisonRow('Current month', valueAt(currentIndex), valueAt(currentIndex - 1), field),
    arComparisonRow('Last 3 months', sumWindow([currentIndex - 2, currentIndex - 1, currentIndex]), sumWindow([currentIndex - 5, currentIndex - 4, currentIndex - 3]), field),
    arComparisonRow('YTD', sumWindow(ytdIndices), sumWindow(priorYtdIndices), field),
    arComparisonRow('Prior year', valueAt(currentIndex), valueAt(currentIndex - 12), field),
  ]
}

// Native availability is independent of coincident legacy numbers. Saved customer
// exclusions remain a presentation scope; they cannot create DSO or native controls.
export function nativeArAgingComponents(period: QuickBooksArTrendPeriod | null) {
  if (!period) return []
  return [
    { label: 'Current invoices', value: Number(period.currentAr || 0) },
    { label: '1–90 days overdue', value: Number(period.under90Ar || 0) },
    { label: '90+ days overdue', value: Number(period.over90Ar || 0) },
    { label: 'Missing due date', value: Number(period.missingDueAr || 0) },
    { label: 'Unapplied credits', value: -Number((period as any).unappliedCreditAr || 0) },
    { label: 'Noninvoice receivables', value: Number((period as any).nonInvoiceAr || 0) },
  ].map(component => ({ ...component, display: formatCurrencyCompact(component.value) }))
}

export function applyNativeAwareArBasis(base: QuickBooksArTrendAnalysis | null, basis: ArBasis, excludedKeys: string[]): QuickBooksArTrendAnalysis | null {
  const adjusted = applyArBasis(base, basis, excludedKeys)
  if (!adjusted || base?.methodology?.source !== 'native-current-postings') return adjusted
  const exclusions = arExcludedKeySet(excludedKeys)
  const excludedCustomers = (base.customerTrends || []).filter(c => exclusions.has(String(c.customerId || '').trim().toLowerCase()) || exclusions.has(String(c.customer || '').trim().toLowerCase()))
  return { ...adjusted, collectability: null, topCustomerConcerns: [], periods: adjusted.periods.map((period, index) => {
    const original = base.periods[index]
    const excluded = basis === 'adjusted' ? excludedCustomers.map(c => base.customerPeriodTrends?.[String(c.customerId || c.customer)]?.find(p => p.periodStart === original.periodStart)).filter(Boolean) : []
    const subtract = (key: string) => Number((original as any)[key] || 0) - excluded.reduce((total, p) => total + Number((p as any)[key] || 0), 0)
    return { ...period, dso: null, averageDso: null, weightedAverageAge: null,
      cashCollections: subtract('cashCollections'), nonInvoiceAr: subtract('nonInvoiceAr'),
      endingArSource: basis === 'adjusted' && excluded.length ? 'native_customer_exclusions' : original.endingArSource,
      agingSource: original.agingSource,
      reconciliationDiagnostics: { ...period.reconciliationDiagnostics, nonInvoiceAr: subtract('nonInvoiceAr') },
    }
  }) }
}

export const buildArInstantCards = (args: {
  periods: any[]
  metricPeriod: any
  selectedBuckets: Array<{ label: string; value: number; display?: string; tone?: string }>
  customerRows: QuickBooksArCustomerConcern[]
  asOfLabel: string
  comparisonKind: MetricComparisonKind
}): CfoInstantCard[] => {
  const { periods, metricPeriod, selectedBuckets, customerRows, asOfLabel, comparisonKind } = args
  if (!metricPeriod) return []
  const sourceFingerprint = arPeriodFingerprint(periods, metricPeriod)
  const cards: CfoInstantCard[] = []
  const metricDefs = [
    { id: 'ar_total', label: 'Total open A/R', field: 'endingAr', value: numberOrNull(metricPeriod?.endingAr), source: 'ar_summary' },
    { id: 'ar_dso', label: 'Days sales outstanding', field: 'dso', value: numberOrNull(metricPeriod?.dso), source: 'ar_dso' },
    { id: 'ar_over90', label: 'A/R over 90 days', field: 'over90Ar', value: numberOrNull(metricPeriod?.over90Ar), source: 'ar_aging_bucket' },
  ]
  for (const metric of metricDefs) {
    if (metric.value == null) continue
    cards.push(buildCfoInstantCardFromContext({
      context: {
        workbookId: null,
        pageId: 'ar',
        activeSection: SURFACE,
        period: trimStr(metricPeriod?.periodEnd || metricPeriod?.periodStart || metricPeriod?.period) || null,
        comparison: comparisonKind,
        asOfLabel,
        metricId: metric.id,
        metricLabel: metric.label,
        sourceComponent: metric.source,
        sourceTables: [QBO_AR_ANALYSIS_TABLE],
        selection: {
          selection_key: `ar:${metric.id}`,
          value: metric.value,
          delta: metric.value - Number(periods[periods.length - 2]?.[metric.field] || 0),
          metric_delta: metric.value - Number(periods[periods.length - 2]?.[metric.field] || 0),
          source_fingerprint: sourceFingerprint,
          artifact_ids: [QBO_AR_ANALYSIS_TABLE],
          buckets: selectedBuckets.slice(0, 4),
          instant_card_trend: arMetricTrend(periods, metric.field, metric.label),
          instant_card_comparisons: arMetricComparisonRows(periods, metricPeriod, metric.field),
          instant_card_insights: [`${metric.label} is ${metric.field === 'dso' ? formatArDays(metric.value) : formatArCurrency(metric.value)} as of ${asOfLabel}.`],
          instant_card_prescriptive_actions: ['Inspect customer contributors and overdue buckets before changing reserve or collection assumptions.'],
          instant_card_next_steps: [
            { kind: 'drilldown', label: 'View customer contributors', prompt: `Show customer contributors for ${metric.label}.`, target: { surface: SURFACE, metricId: metric.id } },
            { kind: 'open_surface', label: 'Open A/R detail', prompt: 'Open A/R customer detail.', target: { surface: SURFACE, metricId: metric.id } },
            { kind: 'compare', label: 'Compare periods', prompt: `Compare ${metric.label} across required periods.`, target: { surface: SURFACE, metricId: metric.id } },
          ],
        },
        grounding: null,
        instantCard: null,
      },
      visual: null,
    }))
  }
  for (const customer of customerRows.slice(0, 5)) {
    const customerId = arCustomerKey(customer)
    const endingAr = numberOrNull(customer.endingAr)
    if (!customerId || endingAr == null) continue
    cards.push(buildCfoInstantCardFromContext({
      context: {
        workbookId: null,
        pageId: 'ar',
        activeSection: SURFACE,
        period: trimStr(metricPeriod?.periodEnd || metricPeriod?.periodStart || metricPeriod?.period) || null,
        comparison: comparisonKind,
        asOfLabel,
        metricId: customerId,
        metricLabel: customer.customer || 'Customer A/R',
        sourceComponent: 'ar_customer',
        sourceTables: [QBO_AR_ANALYSIS_TABLE],
        selection: {
          selection_key: `ar:customer:${customerId}`,
          customer_id: customerId,
          customer: customer.customer,
          value: endingAr,
          overdue_ar: numberOrNull(customer.overdueAr),
          over90_ar: numberOrNull(customer.over90Ar),
          source_fingerprint: sourceFingerprint,
          artifact_ids: [QBO_AR_ANALYSIS_TABLE],
          instant_card_trend: { label: 'Available trend evidence', points: [], unavailable_reason: 'Customer trend is available in the A/R detail packet.' },
          instant_card_comparisons: arMetricComparisonRows(periods, metricPeriod, 'endingAr'),
          instant_card_insights: [`${customer.customer || 'This customer'} has ${formatArCurrency(endingAr)} open A/R${customer.over90Ar ? `, including ${formatArCurrency(numberOrNull(customer.over90Ar) || 0)} over 90 days` : ''}.`],
          instant_card_prescriptive_actions: ['Review open invoices and recent payment behavior before escalating collection actions.'],
          instant_card_next_steps: [
            { kind: 'transactions', label: 'View open invoices', prompt: `Open invoices for ${customer.customer || 'this customer'}.`, target: { surface: SURFACE, customerId } },
            { kind: 'drilldown', label: 'Inspect aging buckets', prompt: `Show aging buckets for ${customer.customer || 'this customer'}.`, target: { surface: SURFACE, customerId } },
          ],
        },
        grounding: null,
        instantCard: null,
      },
      visual: null,
    }))
  }
  return cards
}

const readExcludedKeys = (workbookId: string, connectionId: string): string[] => {
  if (typeof sessionStorage === 'undefined' || !connectionId) return []
  try {
    const raw = sessionStorage.getItem(excludedStorageKey(workbookId, connectionId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.map((value) => trimStr(value)).filter(Boolean) : []
  } catch {
    return []
  }
}

const readSelectedIncomeAccounts = (workbookId: string, connectionId: string): string[] => {
  if (typeof sessionStorage === 'undefined' || !connectionId) return []
  try {
    const raw = sessionStorage.getItem(incomeAccountStorageKey(workbookId, connectionId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.map((value) => trimStr(value)).filter(Boolean) : []
  } catch {
    return []
  }
}

export type CfoArPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string | null) => void
  periodOptions?: CfoShellSelectOption[]
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  onNavSelect: (navId: string) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

export function CfoArPage({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions = [],
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions = [],
  onNavSelect,
  onOpenClassic,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
}: CfoArPageProps) {
  const [baseAnalysis, setBaseAnalysis] = useState<QuickBooksArTrendAnalysis | null>(null)
  const [insights, setInsights] = useState<CfoInsightsResponse | null>(null)
  const [comparisons, setComparisons] = useState<MetricComparisonResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [basis, setBasis] = useState<ArBasis>('reported')
  const [excludedKeys, setExcludedKeys] = useState<string[]>([])
  const [selectedIncomeAccountIds, setSelectedIncomeAccountIds] = useState<string[]>([])
  const [selectedPeriodStart, setSelectedPeriodStart] = useState('')
  const [selectedCustomerKey, setSelectedCustomerKey] = useState('')
  const [aiSection, setAiSection] = useState('overview')
  const [excelStatus, setExcelStatus] = useState('')
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [openDetailSections, setOpenDetailSections] = useState<string[]>([])
  const analysisRef = useRef(baseAnalysis)
  analysisRef.current = baseAnalysis
  const requestEpoch = useRef(0)
  const detailAttemptEpoch = useRef<number | null>(null)
  const detailRequest = useRef<{ epoch: number; promise: Promise<boolean> } | null>(null)

  const { bootstrap, cacheState, analyticsReady, bootstrapLoading, bootstrapError } = useCfoBootstrap(appApi, workbookId)
  const arConnectionId = useMemo(
    () => trimStr(bootstrap?.connection_id || bootstrap?.bindings?.[0]?.connection_id || ''),
    [bootstrap],
  )
  const exportAvailable = typeof workerClient?.export_excel_snapshot === 'function'

  const comparisonsRef = useRef<MetricComparisonResponse | null>(null)
  useEffect(() => {
    comparisonsRef.current = comparisons
  }, [comparisons])

  // Read basis at log time without making the artifact load depend on it (basis
  // is applied client-side after load, so it must not retrigger the fetch).
  const basisRef = useRef(basis)
  useEffect(() => {
    basisRef.current = basis
  }, [basis])

  // Session-scoped customer exclusions, keyed by workbook + connection. Loaded
  // when the scope resolves/changes; persisted by the mutation handlers (not an
  // effect) so a freshly-resolved connection can't momentarily clobber stored
  // exclusions with the empty initial state before the load runs.
  useEffect(() => {
    setExcludedKeys(readExcludedKeys(trimStr(workbookId), arConnectionId))
    setSelectedIncomeAccountIds(readSelectedIncomeAccounts(trimStr(workbookId), arConnectionId))
  }, [workbookId, arConnectionId])

  const persistExcluded = useCallback((keys: string[]) => {
    if (typeof sessionStorage === 'undefined' || !arConnectionId) return
    try {
      sessionStorage.setItem(excludedStorageKey(trimStr(workbookId), arConnectionId), JSON.stringify(keys))
    } catch {
      /* storage may be unavailable; exclusions stay in-memory */
    }
  }, [workbookId, arConnectionId])

  const persistSelectedIncomeAccounts = useCallback((keys: string[]) => {
    if (typeof sessionStorage === 'undefined' || !arConnectionId) return
    try {
      sessionStorage.setItem(incomeAccountStorageKey(trimStr(workbookId), arConnectionId), JSON.stringify(keys))
    } catch {
      /* storage may be unavailable; income-account filters stay in-memory */
    }
  }, [workbookId, arConnectionId])

  // Critical path: read the governed server A/R contract. Server-side artifacts
  // are the source of truth for normal page first paint; local DuckDB is not a
  // fallback for stale or degraded product contracts.
  useEffect(() => {
    let cancelled = false
    requestEpoch.current += 1
    detailRequest.current = null
    setDetailLoading(false)
    setDetailError('')
    setSelectedPeriodStart('')
    setSelectedCustomerKey('')
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this view.')
      return
    }
    if (!arConnectionId && bootstrapLoading) {
      setLoading(true)
      setError('')
      return
    }
    setLoading(true)
    setError('')
    const startedAt = Date.now()
    Promise.resolve()
      .then(async () => {
        if (!arConnectionId && bootstrapError) {
          throw new Error(arBootstrapErrorMessage(bootstrapError))
        }
        let serverContractMessage = ''
        if (typeof (appApi as any).getCfoArAnalysis === 'function' || typeof appApi.getQuickBooksArAnalysis === 'function') {
          try {
            const getArAnalysis = typeof (appApi as any).getCfoArAnalysis === 'function'
              ? (appApi as any).getCfoArAnalysis.bind(appApi)
              : appApi.getQuickBooksArAnalysis.bind(appApi)
            const server = await getArAnalysis(wb, {
              asOf: selectedEndPeriod,
              grain: 'month',
              responseMode: 'summary',
            })
            if (server?.ar_analysis && Array.isArray(server.ar_analysis.periods) && server.ar_analysis.periods.length > 0) {
              cfoArPerfLog('server_contract_ready', {
                workbook_id: wb,
                periods: server.ar_analysis.periods.length,
                readiness: server.contract?.readiness || null,
                freshness_token: server.contract?.freshness_token || null,
                elapsed_ms: Date.now() - startedAt,
              })
              return server.ar_analysis
            }
            serverContractMessage = trimStr(server?.contract?.warnings?.join('; ') || server?.warnings?.join('; '))
          } catch (err: any) {
            serverContractMessage = trimStr(err?.body || err?.message || err)
          }
        }
        throw new Error(serverContractMessage || 'A/R analysis server contract is not ready. FuseGrid is refreshing CFO analytics artifacts; reload this page shortly.')
      })
      .then((analysis) => {
        if (cancelled) return
        setBaseAnalysis(analysis)
        cfoArPerfLog('render_ready', {
          workbook_id: wb,
          periods: analysis.periods?.length ?? 0,
          basis: basisRef.current,
          elapsed_ms: Date.now() - startedAt,
        })
      })
      .catch((err: any) => {
        if (cancelled) return
        const message = trimStr(err?.body || err?.message || err) || 'Could not load A/R.'
        setBaseAnalysis(null)
        setError(message)
        cfoArPerfLog('render_failed', { workbook_id: wb, elapsed_ms: Date.now() - startedAt, error: message })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      requestEpoch.current += 1
    }
  }, [appApi, arConnectionId, bootstrapError, bootstrapLoading, selectedEndPeriod, workbookId])

  // Detail is a prepared-artifact read triggered by an explicit interaction.
  // Concurrent interactions join one request; a changed page scope cannot accept
  // its result, and publication changes require an explicit report reload.
  const ensureFullAr = useCallback(async (): Promise<boolean> => {
    const current = analysisRef.current
    if (!current) return false
    if (current.serving?.mode !== 'summary') return true
    const epoch = requestEpoch.current
    if (detailRequest.current?.epoch === epoch) return detailRequest.current.promise
    detailAttemptEpoch.current = epoch
    setDetailLoading(true)
    setDetailError('')
    const promise = (async () => {
      try {
        const getArAnalysis = typeof (appApi as any).getCfoArAnalysis === 'function'
          ? (appApi as any).getCfoArAnalysis.bind(appApi)
          : appApi.getQuickBooksArAnalysis.bind(appApi)
        const response = await getArAnalysis(trimStr(workbookId), {
          asOf: selectedEndPeriod || current.asOfDate,
          grain: 'month', responseMode: 'full', artifactId: current.serving!.artifactId,
        })
        if (requestEpoch.current !== epoch) return false
        const full = completeArAnalysis(current, response?.ar_analysis)
        analysisRef.current = full
        setBaseAnalysis(full)
        return true
      } catch (err: any) {
        if (requestEpoch.current === epoch) setDetailError(trimStr(err?.body || err?.message || err) || 'Could not load A/R detail.')
        return false
      } finally {
        if (requestEpoch.current === epoch) {
          detailRequest.current = null
          setDetailLoading(false)
        }
      }
    })()
    detailRequest.current = { epoch, promise }
    return promise
  }, [appApi, selectedEndPeriod, workbookId])

  const nativeSource = baseAnalysis?.methodology?.source === 'native-current-postings'
  const summaryOnly = baseAnalysis?.serving?.mode === 'summary'
  // Restore explicit saved filters and sections that the user already opened
  // when navigating to another month. Failures stay visible without retries.
  useEffect(() => {
    if (!loading && summaryOnly && detailAttemptEpoch.current !== requestEpoch.current && (selectedIncomeAccountIds.length > 0
      || (basis === 'adjusted' && excludedKeys.length > 0) || openDetailSections.length > 0)) {
      void ensureFullAr()
    }
  }, [basis, ensureFullAr, excludedKeys.length, loading, openDetailSections.length, selectedIncomeAccountIds.length, summaryOnly])

  // Optional path: insights + metric comparisons. Must never block the page;
  // arrives after the artifact and only enriches cards / AI grounding.
  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb || !arConnectionId || !baseAnalysis) {
      setInsights(null)
      setComparisons(null)
      return
    }
    if (nativeSource || analyticsReady === false) {
      setInsights(null)
      setComparisons(null)
      cfoArPerfLog('optional_context_skipped', { workbook_id: wb, reason: nativeSource ? 'native_optional_families_unavailable' : 'analytics_not_ready' })
      return
    }
    const startedAt = Date.now()
    const asOf = selectedEndPeriod || baseAnalysis.asOfDate || undefined
    const getInsights = typeof (appApi as any).getCfoInsights === 'function'
      ? (appApi as any).getCfoInsights.bind(appApi)
      : appApi.getQuickBooksInsights.bind(appApi)
    const getMetricComparisons = typeof (appApi as any).getCfoMetricComparisons === 'function'
      ? (appApi as any).getCfoMetricComparisons.bind(appApi)
      : appApi.getQuickBooksMetricComparisons.bind(appApi)
    Promise.all([
      getInsights(wb, { asOf, grain: 'month' }).catch(() => null),
      getMetricComparisons(wb, { asOf, grain: 'month', metrics: ['accounts_receivable', 'dso'], comparisons: [comparisonKind] }).catch(() => null),
    ])
      .then(([ins, cmp]) => {
        if (cancelled) return
        setInsights(ins)
        setComparisons(cmp)
        cfoArPerfLog('optional_context_ready', { workbook_id: wb, basis: basisRef.current, elapsed_ms: Date.now() - startedAt })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [analyticsReady, appApi, arConnectionId, baseAnalysis?.asOfDate, baseAnalysis?.serving?.artifactId, nativeSource, comparisonKind, selectedEndPeriod, workbookId])

  useEffect(() => {
    const canonicalAsOf = trimStr(baseAnalysis?.asOfDate || insights?.as_of || comparisons?.as_of || '')
    if (!selectedEndPeriod && canonicalAsOf) onSelectedEndPeriodChange?.(canonicalAsOf)
  }, [baseAnalysis, comparisons, insights, onSelectedEndPeriodChange, selectedEndPeriod])

  // -- basis + derived analysis ---------------------------------------------

  const displayAnalysis = useMemo(() => applyNativeAwareArBasis(baseAnalysis, basis, excludedKeys), [baseAnalysis, basis, excludedKeys])
  const excludedSet = useMemo(() => arExcludedKeySet(excludedKeys), [excludedKeys])
  const basisAdjusted = basis === 'adjusted'

  const { periods, metricPeriod, previousMetricPeriod } = useMemo(
    () => selectArPeriod(displayAnalysis, selectedPeriodStart),
    [displayAnalysis, selectedPeriodStart],
  )
  const latestPeriod = periods.length ? periods[periods.length - 1] : null
  const priorPeriod = periods.length > 1 ? periods[periods.length - 2] : null
  const hasArtifactValues = Boolean(displayAnalysis && latestPeriod)

  const agingBridge = useMemo(() => nativeSource ? null : buildArAgingBridge(priorPeriod, latestPeriod), [nativeSource, priorPeriod, latestPeriod])
  // Evidence buckets follow the user-selected trend period so the AI's bucket
  // mix stays consistent with the `selected_period` it reports.
  const selectedBuckets = useMemo(() => nativeSource ? nativeArAgingComponents(metricPeriod) : arAgingBuckets(metricPeriod), [nativeSource, metricPeriod])
  const total = numberOrNull(latestPeriod?.endingAr)
  // Headline DSO anchors to the newest CLOSED month (skipping a trailing partial
  // month), matching the server's `headline_dso_from_analysis` and therefore the
  // DSO instant card, CFO Home, and the firm cockpit. A/R total/overdue stay on
  // the latest period; DSO is inherently a closed-period metric, and its tile is
  // labelled with the month it belongs to so the mixed period is explicit.
  const dsoAnchor = useMemo(() => arHeadlineDsoAnchor(displayAnalysis), [displayAnalysis])
  const dso = dsoAnchor.dso
  const dsoDelta = dso != null && dsoAnchor.prior != null ? dso - dsoAnchor.prior : null
  const dsoPeriodLabel = dsoAnchor.period ? formatArTrendPeriodLabel(dsoAnchor.period) : ''
  const overdueAr = numberOrNull(latestPeriod?.overdueAr)
  const weightedAge = numberOrNull(latestPeriod?.weightedAverageAge)

  const topRisks = useMemo<QuickBooksArCustomerConcern[]>(() => displayAnalysis?.topCustomerConcerns || [], [displayAnalysis])
  const incomeAccountSelectionSet = useMemo(() => new Set(nativeSource ? [] : selectedIncomeAccountIds), [nativeSource, selectedIncomeAccountIds])
  // When the income-account filter is active, the customer/change-driver tables
  // are rebuilt from the same allocation rows that power the income-account
  // report, and the invoice table keeps only invoices carrying a selected
  // account — so every table reflects the same selected slice of A/R.
  const customerRows = useMemo(
    () =>
      incomeAccountSelectionSet.size
        ? arIncomeAccountCustomerRowsForPeriod(displayAnalysis, metricPeriod, incomeAccountSelectionSet, basisAdjusted ? excludedSet : null)
        : arCustomerRowsForPeriod(displayAnalysis, metricPeriod),
    [displayAnalysis, metricPeriod, incomeAccountSelectionSet, basisAdjusted, excludedSet],
  )
  const changeDrivers = useMemo(
    () =>
      incomeAccountSelectionSet.size
        ? arIncomeAccountChangeDrivers(displayAnalysis, metricPeriod, previousMetricPeriod, incomeAccountSelectionSet, excludedSet)
        : buildArCustomerChangeDrivers(displayAnalysis, metricPeriod, previousMetricPeriod, excludedSet),
    [displayAnalysis, metricPeriod, previousMetricPeriod, excludedSet, incomeAccountSelectionSet],
  )
  const invoiceRows = useMemo(
    () =>
      arFilterInvoicesByIncomeAccounts(
        arInvoiceRowsForSelection(displayAnalysis, metricPeriod, selectedCustomerKey),
        displayAnalysis,
        metricPeriod,
        incomeAccountSelectionSet,
      ),
    [displayAnalysis, metricPeriod, selectedCustomerKey, incomeAccountSelectionSet],
  )
  const incomeAccountView = useMemo(
    () => arIncomeAccountRowsForPeriod(displayAnalysis, metricPeriod, incomeAccountSelectionSet),
    [displayAnalysis, metricPeriod, incomeAccountSelectionSet],
  )
  const trendPeriods = useMemo(
    () => arIncomeAccountTrendPeriods(displayAnalysis, periods, incomeAccountSelectionSet),
    [displayAnalysis, periods, incomeAccountSelectionSet],
  )
  const incomeAccountTrendFiltered = !nativeSource && selectedIncomeAccountIds.length > 0
  const collectabilityView = useMemo(
    () => arCollectabilityView(displayAnalysis?.collectability, excludedSet, basisAdjusted),
    [displayAnalysis, excludedSet, basisAdjusted],
  )
  const reserveSignal = useMemo(() => arReserveSignal(displayAnalysis?.collectability, collectabilityView), [displayAnalysis, collectabilityView])
  const excludedSummary = useMemo(() => arExcludedSummary(displayAnalysis), [displayAnalysis])
  const excludedAmount = useMemo(() => arExcludedArAmount(displayAnalysis), [displayAnalysis])

  const customerNameByKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const customer of customerRows) map.set(arCustomerKey(customer), customer.customer || '')
    for (const driver of changeDrivers) map.set(String(driver.customerId || driver.customer), driver.customer)
    return map
  }, [customerRows, changeDrivers])
  const selectedCustomerName = selectedCustomerKey ? customerNameByKey.get(selectedCustomerKey) || '' : ''

  // The date the A/R data actually covers. The shared header period picker is
  // driven by statement/forecast periods and can run ahead of this connection's
  // A/R (the artifact's as-of already reflects the freshest A/R activity that
  // exists), so labels that describe the data must key off the data's real
  // as-of — NOT the requested `selectedEndPeriod` — or the page shows e.g. an
  // "Apr 2026" trend under a "May 2026" header.
  const dataAsOfDate = useMemo(
    () => trimStr(arPeriodEndDate(latestPeriod) || displayAnalysis?.asOfDate || ''),
    [latestPeriod, displayAnalysis],
  )
  const asOfLabel = useMemo(
    () => formatAsOf(dataAsOfDate || selectedEndPeriod || insights?.as_of || null),
    [dataAsOfDate, selectedEndPeriod, insights],
  )
  // The header selection is beyond the A/R data's coverage (e.g. May selected,
  // but A/R only runs through April). When true, we surface a calm note instead
  // of silently showing prior-month figures under a later-month header.
  const coverageGap = useMemo(() => {
    const selectedMonth = trimStr(selectedEndPeriod).slice(0, 7)
    const dataMonth = dataAsOfDate.slice(0, 7)
    return Boolean(selectedMonth && dataMonth && selectedMonth > dataMonth)
  }, [selectedEndPeriod, dataAsOfDate])
  const selectedHeaderMonthLabel = useMemo(
    () => formatArTrendPeriodLabel({ periodEnd: trimStr(selectedEndPeriod) } as QuickBooksArTrendPeriod),
    [selectedEndPeriod],
  )
  const dataThroughMonthLabel = useMemo(
    () => formatArTrendPeriodLabel(latestPeriod) || asOfLabel,
    [latestPeriod, asOfLabel],
  )
  const selectedPeriodLabel = formatArTrendPeriodLabel(metricPeriod) || asOfLabel
  const rangeLabel = previousMetricPeriod && metricPeriod
    ? `${formatArTrendPeriodLabel(previousMetricPeriod)} → ${formatArTrendPeriodLabel(metricPeriod)}`
    : selectedPeriodLabel
  const overviewBridgeLabel = agingBridge
    ? `${agingBridge.startLabel || 'Start'} → ${agingBridge.endLabel || 'End'}`
    : asOfLabel

  // -- handlers --------------------------------------------------------------

  const handleExclude = useCallback(async (customer: { customerId?: string | null; customer?: string | null }) => {
    const key = String(customer?.customerId || customer?.customer || '').trim()
    if (!key || !await ensureFullAr()) return
    setExcludedKeys((current) => {
      const next = current.includes(key) ? current : [...current, key]
      persistExcluded(next)
      return next
    })
    setBasis('adjusted')
    setSelectedCustomerKey((current) => (current === key ? '' : current))
  }, [ensureFullAr, persistExcluded])

  const handleInclude = useCallback((customerKey: string) => {
    const key = customerKey.trim().toLowerCase()
    setExcludedKeys((current) => {
      const next = current.filter((value) => value.trim().toLowerCase() !== key)
      persistExcluded(next)
      return next
    })
  }, [persistExcluded])

  const handleSelectedIncomeAccountsChange = useCallback(async (keys: string[]) => {
    if (!await ensureFullAr()) return
    const next = Array.from(new Set(keys.map((key) => trimStr(key)).filter(Boolean)))
    setSelectedIncomeAccountIds(next)
    persistSelectedIncomeAccounts(next)
  }, [ensureFullAr, persistSelectedIncomeAccounts])

  const handleSelectCustomer = useCallback(async (key: string) => {
    if (await ensureFullAr()) setSelectedCustomerKey((current) => current === key ? '' : key)
  }, [ensureFullAr])

  const exportTable: CfoArExportTable = useCallback((args: Parameters<CfoArExportTable>[0]) => {
    void ensureFullAr().then((ready) => {
      if (ready) return downloadCfoArTableExcel({ ...args, workerClient: workerClient ?? undefined, setStatus: setExcelStatus })
    })
  }, [ensureFullAr, workerClient])

  // -- AI evidence selection -------------------------------------------------

  const analysisSelection = useMemo(
    () => {
      const selection = buildArAnalysisSelection({
        sourceTable: nativeSource ? 'native-current-postings' : QBO_AR_ANALYSIS_TABLE,
        asOf: displayAnalysis?.asOfDate || selectedEndPeriod || null,
        basis,
        section: aiSection,
        selectedPeriodEnd: trimStr((metricPeriod as any)?.periodEnd) || displayAnalysis?.asOfDate || null,
        buckets: selectedBuckets,
        topRisks,
        excluded: excludedSummary,
        excludedArAmount: excludedAmount,
        reserveSignal,
      })
      return nativeSource ? { ...selection, evidence_scope: 'native_ar_aging',
        feature_availability: baseAnalysis?.methodology?.featureAvailability,
        source_identity: { generation: baseAnalysis?.methodology?.sourceGeneration, revision: baseAnalysis?.methodology?.sourceRevision, job_id: baseAnalysis?.methodology?.sourceJobId },
      } : selection
    },
    [baseAnalysis, displayAnalysis, nativeSource, selectedEndPeriod, basis, aiSection, metricPeriod, selectedBuckets, topRisks, excludedSummary, excludedAmount, reserveSignal],
  )
  const arInstantCards = useMemo(
    () => !displayAnalysis || nativeSource ? [] : buildArInstantCards({
      periods,
      metricPeriod,
      selectedBuckets,
      customerRows,
      asOfLabel,
      comparisonKind,
    }),
    [displayAnalysis, nativeSource, asOfLabel, comparisonKind, customerRows, metricPeriod, periods, selectedBuckets],
  )
  const arPageFactsFingerprint = useMemo(
    () => arPeriodFingerprint(periods, metricPeriod),
    [metricPeriod, periods],
  )

  const heroHeadline =
    total != null
      ? `${formatCurrencyCompact(total)} outstanding${dso != null ? `, DSO ~${dso.toFixed(0)} days` : ''}`
      : 'Your accounts receivable'
  const heroSubtext =
    dsoDelta != null
      ? `Days sales outstanding ${dsoDelta >= 0 ? 'rose' : 'fell'} ${Math.abs(dsoDelta).toFixed(0)} day${Math.abs(dsoDelta) === 1 ? '' : 's'} vs. last month. Who owes you, in calling order, is below.`
      : nativeSource ? 'Exact dated receivables, customer balances, and open invoice aging are below.' : 'Total open receivable and how fast it collects — who owes you, in calling order, is below.'

  const depthSection = (id: string, props: Parameters<typeof CfoDepthSection>[0]) => (
    <CfoDepthSection
      {...props}
      surface={SURFACE}
      workbookId={workbookId}
      children={() => summaryOnly && (['income_accounts', 'invoices', 'collectability'].includes(id)
        || (id === 'customers' && selectedIncomeAccountIds.length > 0))
        ? <div role="status">{detailError || 'Loading A/R detail…'}</div>
        : props.children()}
      onToggle={(open) => {
        if (['income_accounts', 'invoices', 'collectability'].includes(id)) {
          setOpenDetailSections((current) => open ? [...new Set([...current, id])] : current.filter((value) => value !== id))
          if (open) void ensureFullAr()
        }
        setAiSection((current) => (open ? id : current === id ? 'overview' : current))
        props.onToggle?.(open)
      }}
    />
  )

  const incomeAccountFilter = () => nativeSource ? null : summaryOnly ? (
    <div className="cfo-ar-income-filter is-compact">
      <button type="button" disabled={detailLoading} onClick={() => { void ensureFullAr() }}>
        {detailLoading ? 'Loading income accounts…' : 'Filter by income account'}
      </button>
    </div>
  ) : (
    <CfoArIncomeAccountFilter view={incomeAccountView} selectedAccountIds={selectedIncomeAccountIds}
      onSelectedAccountIdsChange={handleSelectedIncomeAccountsChange} compact />
  )

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="ar"
      activeSection={SURFACE}
      runtimeProfile={runtimeProfile}
      eyebrow="Understand"
      title="A/R &amp; collectability"
      lede={
        <>
          “How much you’re owed, how old it is, and <em>which customers to chase.</em>”
        </>
      }
      crumb={
        <>
          <b>A/R &amp; collectability</b> · {basisAdjusted ? 'Adjusted basis' : 'Reported basis'}
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={selectedEndPeriod || displayAnalysis?.asOfDate || insights?.as_of || undefined}
      onPeriodChange={onSelectedEndPeriodChange}
      periodAriaLabel="Select CFO Core end period"
      comparisonLabel={comparisonOptions.find((option) => option.id === comparisonKind)?.label || 'vs. prior month'}
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) => onComparisonKindChange?.(value as MetricComparisonKind)}
      comparisonAriaLabel="Select CFO Core comparison period"
      cacheState={cacheState}
      pageLoading={loading || bootstrapLoading}
      pageLoadingLabel="Loading A/R collectability context…"
      copilotSubtitle="reading your A/R aging"
      copilotPlaceholder="Ask about A/R…"
      copilotIntro={nativeSource ? 'You’re on native A/R. Ask about dated aging, customer balances, or open invoices. Collectability and forecast metrics are unavailable.' : 'You’re on A/R. Ask me to rank clients by risk, explain DSO, or trace a balance to its open invoices.'}
      askPlaceholder="Ask about collectability…"
      suggestedPrompts={nativeSource ? ['Who owes the 90+ balances?', 'Which invoices are overdue?'] : ['Who owes the 90+ balances?', 'Why did DSO change?', 'Which clients are most at risk?']}
      getComparisons={() => !baseAnalysis || nativeSource ? null : comparisonsRef.current}
      sourceTables={!baseAnalysis || nativeSource ? [] : [QBO_AR_ANALYSIS_TABLE]}
      analysisSelection={analysisSelection}
      instantCards={arInstantCards}
      pageFactsFingerprint={arPageFactsFingerprint}
      duckDbOpts={null}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {() => (
        <>
          {summaryOnly && (selectedIncomeAccountIds.length > 0 || (basisAdjusted && excludedKeys.length > 0)) ? (
            <div className="cfo-shell-notice" role={detailError ? 'alert' : 'status'}>
              {detailError || 'Loading your selected A/R view…'}
            </div>
          ) : !hasArtifactValues && error ? (
            <section className="hero rise">
              <div className="hero-top">
                <div>
                  <h1>{isArNoDataForPeriod(error) ? 'No A/R activity for this period' : arLoadErrorLead(error)}</h1>
                </div>
              </div>
              <div
                className={isArNoDataForPeriod(error) ? 'cfo-shell-notice' : 'cfo-shell-notice error'}
                role={isArNoDataForPeriod(error) ? 'status' : 'alert'}
                style={{ marginTop: 'var(--s3)' }}
              >
                {error}
              </div>
              {classicEscapes ? (
                <div className="cfo-ar-rebuild-actions">
                  <button type="button" className="btn" onClick={onOpenClassic}>Open A/R workspace</button>
                </div>
              ) : null}
            </section>
          ) : (
            <>
              <section className="hero rise">
                <div className="hero-top">
                  <div>
                    <h1>{heroHeadline}</h1>
                    <p>{heroSubtext}</p>
                    <span className="tag">{nativeSource ? 'Native source · dated A/R controls' : 'AI summary · from your accounting A/R'}</span>
                  </div>
                </div>
              </section>

              <div className="cfo-ar-kpis">
                <article className="card"><div className="body"><div className="grow"><h3>{total != null ? formatCurrencyCompact(total) : '—'}</h3><p>Total open A/R</p></div></div></article>
                <article className="card"><div className="body"><div className="grow"><h3>{dso != null ? `${dso.toFixed(0)} days` : '—'}</h3><p>Days sales outstanding{dsoPeriodLabel ? ` · ${dsoPeriodLabel}` : ''}{dsoDelta != null ? ` · ${dsoDelta >= 0 ? '+' : '−'}${Math.abs(dsoDelta).toFixed(0)}d MoM` : ''}</p></div></div></article>
                <article className="card"><div className="body"><div className="grow"><h3>{overdueAr != null ? formatCurrencyCompact(overdueAr) : 'N/A'}</h3><p>Overdue A/R</p></div></div></article>
                <article className="card"><div className="body"><div className="grow"><h3>{weightedAge != null ? formatArDays(weightedAge) : 'N/A'}</h3><p>Weighted average age</p></div></div></article>
                <article className="card"><div className="body"><div className="grow"><h3>{reserveSignal.hasBenchmarks ? formatCurrencyCompact(reserveSignal.recommendedReserveAmount ?? reserveSignal.expectedWriteOff) : '—'}</h3><p>Reserve signal</p></div></div></article>
              </div>
              {nativeSource ? <div className="cfo-shell-notice" role="status">Native aging and open invoice details are available. DSO, weighted age, collectability, service/item allocations, and forecast metrics are unavailable in this source contract.</div> : <CfoAiRead>
                <b>DSO</b> matters because it shows how much of the sales engine is still tied up in receivables: if sales rise faster than A/R, DSO can improve even with old invoices still open. <b>Weighted average age</b> matters because it shows the risk inside the receivable book itself: each open dollar is weighted by how long it has been outstanding, so a few large stale invoices can pull it up and signal collection or reserve pressure.
              </CfoAiRead>}

              <CfoControlRow>
                <CfoSegmentedControl
                  label="A/R basis"
                  value={basis}
                  onChange={(value) => { void ensureFullAr().then((ready) => { if (ready) setBasis(value as ArBasis) }) }}
                  options={[
                    { id: 'reported', label: 'Reported' },
                    { id: 'adjusted', label: 'Adjusted' },
                  ]}
                />
                <button type="button" className="cfo-shell-chip">
                  As of {asOfLabel}{coverageGap ? ' · latest available' : ''}
                </button>
                <button type="button" className="cfo-shell-chip">Selected · {selectedPeriodLabel}</button>
              </CfoControlRow>

              {coverageGap ? (
                <div className="cfo-shell-notice" role="status" style={{ marginTop: 'var(--s3)' }}>
                  A/R is current through <strong>{dataThroughMonthLabel}</strong>. No {selectedHeaderMonthLabel} A/R
                  activity has synced from your accounting platform yet, so the figures, trend, and tables below reflect {dataThroughMonthLabel}.
                </div>
              ) : null}

              {basisAdjusted ? (
                <div className="cfo-ar-basis-banner" role="status">
                  <div className="cfo-ar-basis-banner-head">
                    <strong>Adjusted basis</strong>
                    <span>
                      {(excludedSummary.length || excludedKeys.length)
                        ? `${excludedSummary.length || excludedKeys.length} customer${(excludedSummary.length || excludedKeys.length) === 1 ? '' : 's'} excluded · ${formatArCurrency(excludedAmount)} removed`
                        : 'No customers excluded yet — use the Exclude action in Customers or Change drivers.'}
                    </span>
                    <button type="button" className="btn ghost" onClick={() => setBasis('reported')}>Return to reported basis</button>
                  </div>
                  {excludedSummary.length ? (
                    <div className="cfo-ar-basis-chips">
                      {excludedSummary.map((customer) => {
                        const key = String(customer.customerId || customer.customer || '').trim()
                        return (
                          <span key={key || customer.customer} className="cfo-ar-basis-chip">
                            <span>{customer.customer} ({formatArCurrency(customer.endingAr)})</span>
                            <button type="button" onClick={() => handleInclude(key)} title={`Include ${customer.customer} in A/R analysis`}>Include</button>
                          </span>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {displayAnalysis?.warnings?.length ? (
                <div className="cfo-shell-notice" style={{ marginTop: 'var(--s3)' }}>{displayAnalysis.warnings.join(' ')}</div>
              ) : null}

              {detailLoading || detailError ? (
                <div className="cfo-shell-notice" role={detailError ? 'alert' : 'status'}>
                  {detailError || 'Loading A/R detail…'}
                </div>
              ) : null}

              {excelStatus ? (
                <div className={`cfo-ar-export-status${/failed|unavailable/i.test(excelStatus) ? ' is-error' : ''}`} role="status">{excelStatus}</div>
              ) : null}

              {/* Lead with the promised object: the who-owes-you customer list,
                  open by default. Analysis layers (trend, change drivers, the
                  aging bridge) follow below. */}
              {depthSection('customers', {
                title: 'Who owes you',
                meta: 'Sortable aging and risk by customer — in calling order',
                showLabel: 'Show the customer aging',
                defaultOpen: true,
                children: () => (
                  <CfoArCustomersSection
                    customers={customerRows}
                    candidates={customerRows}
                    metaLabel={selectedPeriodLabel}
                    selectedCustomerKey={selectedCustomerKey}
                    onSelectCustomer={handleSelectCustomer}
                    onExcludeCustomer={handleExclude}
                    exportAvailable={exportAvailable}
                    onExport={exportTable}
                    filter={incomeAccountFilter()}
                  />
                ),
              })}

              {depthSection('trend', {
                title: 'The A/R trend',
                meta: 'Stacked aging with DSO and weighted-age overlays',
                showLabel: 'Show the A/R trend',
                defaultOpen: true,
                hideToggle: true,
                children: () => nativeSource ? <section className="cfo-ar-card">
                  <div className="cfo-shell-notice" role="status">The legacy stacked trend is unavailable for native A/R because signed credits and noninvoice balances must remain separate from gross invoice aging.</div>
                  <table aria-label="Native A/R signed aging components"><thead><tr><th>Component · {selectedPeriodLabel}</th><th>Amount</th></tr></thead><tbody>{nativeArAgingComponents(metricPeriod).map(component => <tr key={component.label}><td>{component.label}</td><td>{component.display}</td></tr>)}</tbody></table>
                </section> :
                  periods.length ? (
                    <section className="cfo-ar-card">
                      <div className="cfo-ar-section-bar">
                        <div className="cfo-ar-section-bar-title">
                          {selectedCustomerName || (incomeAccountTrendFiltered ? 'Selected income-account' : 'Overall')} A/R trend
                        </div>
                        <div className="cfo-ar-section-bar-end"><span className="cfo-ar-section-bar-meta">{rangeLabel}</span></div>
                      </div>
                      {incomeAccountFilter()}
                      <CfoArTrendChart
                        periods={trendPeriods}
                        selectedPeriodStart={selectedPeriodStart}
                        onSelectPeriod={(start) => { void ensureFullAr().then((ready) => { if (ready) setSelectedPeriodStart((current) => current === start ? '' : start) }) }}
                        customerName={selectedCustomerName || (incomeAccountTrendFiltered ? 'Selected income accounts' : null)}
                        className="cfo-ar-trend-frame"
                      />
                      <div className="cfo-ar-trend-legend">
                        <span><i style={{ background: 'var(--pos, #2f855a)' }} /> Current</span>
                        <span><i style={{ background: 'var(--check-acc, #d69e2e)' }} /> 1-90</span>
                        <span><i style={{ background: 'var(--neg, #b91c1c)' }} /> 90+</span>
                        {incomeAccountTrendFiltered || nativeSource ? null : <span><i style={{ background: '#176b87' }} /> DSO</span>}
                        {nativeSource ? null : <span><i className="dashed" style={{ background: '#7a5c2e' }} /> Weighted age</span>}
                      </div>
                    </section>
                  ) : (
                    <div className="cfo-shell-notice">No A/R periods are available for this selection.</div>
                  ),
              })}

              {depthSection('change_drivers', {
                title: 'What changed',
                meta: 'Customer-level A/R change for the selected period',
                showLabel: 'Show the change drivers',
                children: () => (
                  <CfoArChangeDriversSection
                    drivers={changeDrivers}
                    rangeLabel={rangeLabel}
                    selectedCustomerKey={selectedCustomerKey}
                    onSelectCustomer={handleSelectCustomer}
                    onExcludeCustomer={handleExclude}
                    exportAvailable={exportAvailable}
                    onExport={exportTable}
                    filter={incomeAccountFilter()}
                  />
                ),
              })}

              {depthSection('aging_bridge', {
                title: 'The aging bridge',
                meta: 'How the bucket mix moved from the prior period',
                showLabel: 'Show the aging bridge',
                children: () => nativeSource ? <div className="cfo-shell-notice" role="status">The legacy aging bridge is unavailable for native A/R; gross aging, unapplied credits, and noninvoice balances retain their exact signed components.</div> :
                  agingBridge ? (
                    <figure className="figure">
                      <div className="fig-head">
                        <span className="fnum">Fig. 1</span>
                        <span className="ftitle">A/R aging bridge</span>
                        <span className="fmeta">{overviewBridgeLabel}</span>
                        <PinToReportButton
                          buildPayload={() => ({
                            // ar_summary is a native block kind: the presentation
                            // renders the full aging + collectability section from
                            // its own A/R analysis artifact.
                            title: 'A/R aging & collectability',
                            kind: 'ar_summary',
                            section: 'accountsReceivable',
                          })}
                        />
                      </div>
                      <ArAgingBridgeChart
                        className="fig-svg"
                        ariaLabel={`Accounts receivable aging bridge from ${overviewBridgeLabel}, with bucket changes between the beginning and ending bars`}
                        bridge={agingBridge}
                        formatValue={(v) => formatCurrencyCompact(v)}
                      />
                      <CfoAiRead>
                        The bridge starts with the beginning bucket mix, steps through what moved up or down by aging bucket, and lands on the ending A/R balance.
                      </CfoAiRead>
                    </figure>
                  ) : (
                    <div className="cfo-shell-notice" role="status">
                      The aging bridge needs two consecutive periods of prepared A/R data. It will appear once a prior month is available to compare against.
                    </div>
                  ),
              })}

              {depthSection('income_accounts', {
                title: 'Customers & income accounts',
                meta: 'Aged receivables classified by invoice-line income account',
                showLabel: 'Show customers & income accounts',
                children: () => nativeSource ? <div className="cfo-shell-notice" role="status">Service/item and income-account allocations are unavailable for native A/R.</div> : (
                  <CfoArIncomeAccountSection
                    view={incomeAccountView}
                    selectedAccountIds={selectedIncomeAccountIds}
                    onSelectedAccountIdsChange={handleSelectedIncomeAccountsChange}
                    metaLabel={selectedPeriodLabel}
                    exportAvailable={exportAvailable}
                    onExport={exportTable}
                  />
                ),
              })}

              {depthSection('invoices', {
                title: 'The invoices',
                meta: 'Open invoice drilldown for the selected period and customer',
                showLabel: 'Show the invoice detail',
                children: () => (
                  <CfoArInvoicesSection
                    invoices={invoiceRows}
                    headerLabel={`${selectedCustomerName || 'All customers'} · ${selectedPeriodLabel}`}
                    metaLabel={`${selectedCustomerName ? `${selectedCustomerName} | ` : 'All customers | '}${selectedPeriodLabel}`}
                    exportAvailable={exportAvailable}
                    onExport={exportTable}
                    filter={incomeAccountFilter()}
                  />
                ),
              })}

              {depthSection('collectability', {
                title: 'Collectability & reserve',
                meta: 'At-risk A/R, recovery thresholds, and the bad-debt reserve',
                showLabel: 'Show collectability & reserve',
                children: () => nativeSource ? <div className="cfo-shell-notice" role="status">Collectability and reserve benchmarks are unavailable for native A/R.</div> : (
                  <CfoArCollectabilitySection
                    collectability={displayAnalysis?.collectability}
                    view={collectabilityView}
                    exportAvailable={exportAvailable}
                    onExport={exportTable}
                  />
                ),
              })}

              {classicEscapes ? (
                <div className="cfo-ar-rebuild-actions">
                  <button type="button" className="btn ghost" onClick={onOpenClassic}>Open A/R review</button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </CfoInteriorPage>
  )
}

export default CfoArPage
